import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  FileText,
  Loader2,
  Mail,
  MessageCircle,
  MessageSquare,
  Paperclip,
  RefreshCw,
  RotateCcw,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { TemplateSendDialog } from "@/components/portal/messaging/TemplateSendDialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import {
  platformApi,
  type MessagingConversation,
  type MessagingMessage,
} from "@/services/api/platform";
import { formatTimeAgo } from "@/lib/dateFormat";

export const Route = createFileRoute("/portal/messaging/inbox")({
  component: InboxPage,
});

const POLL_INTERVAL_MS = 10_000;

function channelIcon(channel: string) {
  switch (channel) {
    case "email":
      return Mail;
    case "whatsapp":
      return MessageCircle;
    case "teams":
      return MessageSquare;
    default:
      return MessageSquare;
  }
}

function formatRelativeTime(iso?: string | null): string {
  if (!iso) return "";
  return formatTimeAgo(iso);
}

function InboxPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId;

  const [conversations, setConversations] = useState<MessagingConversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  // ME40 — how many the org actually has, so the UI can never imply the list is
  // complete when it is not.
  const [conversationsTotal, setConversationsTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessagingMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  // ME41 — which failed message is currently being resent (per-message, so one
  // retry does not disable every other retry button in the thread).
  const [retryingId, setRetryingId] = useState<string | null>(null);
  // ME41 follow-up: the out-of-window recovery path. Retry resends the same
  // plain text, so when the failure IS the 24h window it fails again
  // identically — an approved template is the only send Meta will accept.
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  // NOT gated on can("edit", "messaging"), and that is deliberate — an earlier
  // revision of this PR did gate it and was wrong.
  //
  // Authorisation for this view lives at the CP gateway, per PATH, in Casbin.
  // Prod policy grants org_member `read` on /gateway/messaging/* but explicitly
  // grants `(create|read|update)` on /gateway/messaging/api/v1/conversations/*
  // — i.e. members may reply in conversations while not administering
  // messaging. The gateway enforces that before the request reaches the DP
  // service.
  //
  // The FE feature matrix cannot express it: `messaging` has no rbac_paths in
  // services.yaml, so it defaults to /api/v1/gateway/messaging/ and resolves
  // against the read-only rule. can("edit","messaging") is therefore FALSE for
  // org_member — gating on it would hide reply/template from the very support
  // agents the conversations policy exists to permit.
  //
  // Hence the composer, Send and Retry are ungated too. That is not an
  // oversight; the gate is one layer up.
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === selectedId) ?? null,
    [conversations, selectedId],
  );

  // ME40 — the API caps `limit` at 100 (`le=100` on the endpoint), so the old
  // fixed `limit: 100` was not a lazy default that could be raised: it was the
  // maximum a single request can return. Conversation #101 simply did not exist
  // as far as the UI was concerned — no count, no control, no indication that
  // anything had been left out. Silent truncation of a customer's data is a
  // correctness bug, not a UX gap.
  const CONVERSATIONS_PAGE_SIZE = 100;

  const fetchConversations = useCallback(
    async (silent = false) => {
      if (!token || !organisationId) return;
      if (!silent) setConversationsLoading(true);
      try {
        const response = await platformApi.listConversations(token, organisationId, {
          limit: CONVERSATIONS_PAGE_SIZE,
          offset: 0,
        });
        const firstPage = response.items || [];
        setConversationsTotal(response.total ?? firstPage.length);
        // Neither a poll NOR an explicit refresh may throw away pages the user
        // has already loaded — one rule, so the list never shrinks under them.
        // (Keying this on `silent` would mean the Refresh button silently
        // collapsed 137 back to 100.)
        //
        // The list is ordered by LastMessageAt desc, so an active thread moves
        // INTO page 1 and must not then appear twice: merge by id, first page
        // wins, and keep any previously-loaded conversation that has dropped
        // out of it.
        setConversations((prev) => {
          if (prev.length <= firstPage.length) return firstPage;
          const seen = new Set(firstPage.map((c) => c.id));
          return [...firstPage, ...prev.filter((c) => !seen.has(c.id))];
        });
      } catch (err) {
        // Silent polls shouldn't toast — only initial loads
        if (!silent) {
          console.error("Failed to load conversations:", err);
          toast.error("Failed to load conversations");
        }
      } finally {
        if (!silent) setConversationsLoading(false);
      }
    },
    [token, organisationId],
  );

  const loadMoreConversations = useCallback(async () => {
    if (!token || !organisationId) return;
    setLoadingMore(true);
    try {
      const response = await platformApi.listConversations(token, organisationId, {
        limit: CONVERSATIONS_PAGE_SIZE,
        offset: conversations.length,
      });
      const nextPage = response.items || [];
      setConversationsTotal(response.total ?? conversations.length + nextPage.length);
      // Offset paging over a list ordered by recency can repeat a row if a
      // conversation moves between requests — dedupe rather than render it twice.
      setConversations((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        return [...prev, ...nextPage.filter((c) => !seen.has(c.id))];
      });
    } catch (err) {
      console.error("Failed to load more conversations:", err);
      toast.error("Failed to load more conversations");
    } finally {
      setLoadingMore(false);
    }
  }, [token, organisationId, conversations.length]);

  const fetchMessages = useCallback(
    async (conversationId: string, silent = false) => {
      if (!token) return;
      if (!silent) setMessagesLoading(true);
      try {
        const response = await platformApi.listConversationMessages(
          token,
          conversationId,
          { limit: 200 },
        );
        setMessages(response.items || []);
      } catch (err) {
        if (!silent) {
          console.error("Failed to load messages:", err);
          toast.error("Failed to load messages");
        }
      } finally {
        if (!silent) setMessagesLoading(false);
      }
    },
    [token],
  );

  // Initial conversations load + 10s background refresh
  useEffect(() => {
    fetchConversations();
    const id = window.setInterval(() => fetchConversations(true), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [fetchConversations]);

  // When a conversation is selected: fetch its messages, mark read, then poll
  useEffect(() => {
    if (!selectedId || !token) return;
    fetchMessages(selectedId);
    // Mark read (fire-and-forget). Refresh conversations so unread badge clears.
    platformApi
      .markConversationRead(token, selectedId)
      .then(() => fetchConversations(true))
      .catch(() => {
        // Mark-read isn't critical to the UX — log and move on.
      });
    const id = window.setInterval(() => fetchMessages(selectedId, true), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [selectedId, token, fetchMessages, fetchConversations]);

  // Auto-scroll thread to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages.length]);

  const handleSend = useCallback(async () => {
    if (!token || !selectedId) return;
    const content = replyText.trim();
    if (!content) return;
    setSending(true);
    try {
      await platformApi.sendConversationMessage(token, selectedId, {
        content,
        content_type: "text",
      });
      setReplyText("");
      // Send is async on the backend — fetch shortly after to surface the outbound row.
      window.setTimeout(() => fetchMessages(selectedId, true), 1_000);
    } catch (err) {
      console.error("Failed to send message:", err);
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : "Failed to send message",
      );
    } finally {
      setSending(false);
    }
  }, [token, selectedId, replyText, fetchMessages]);

  // ME41 — resend a failed message. There is no dedicated retry endpoint and it
  // does not need one: a retry IS a send of the same content, so this reuses the
  // exact path the composer uses. Anything cleverer (server-side requeue of the
  // original row) would duplicate delivery semantics that already exist.
  const handleRetry = useCallback(
    async (message: MessagingMessage) => {
      if (!token || !selectedId) return;
      const content = (message.content ?? "").trim();
      if (!content) {
        // A media-only message has nothing to resend as text; sending "" would
        // be rejected by the API and read as a mystery failure.
        toast.error("This message has no text content to resend.");
        return;
      }
      setRetryingId(message.id);
      try {
        await platformApi.sendConversationMessage(token, selectedId, {
          content,
          content_type: message.content_type || "text",
        });
        toast.success("Message resent");
        window.setTimeout(() => fetchMessages(selectedId, true), 1_000);
      } catch (err) {
        console.error("Failed to resend message:", err);
        toast.error(
          err instanceof Error && err.message
            ? err.message
            : "Failed to resend message",
        );
      } finally {
        setRetryingId(null);
      }
    },
    [token, selectedId, fetchMessages],
  );

  const handleReplyKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter to send, Shift+Enter for newline
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Inbox</h1>
          <p className="mt-2 text-lg text-slate-600">
            Unified inbox — Email, WhatsApp, Teams
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchConversations()}
          disabled={conversationsLoading}
        >
          {conversationsLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white shadow-sm overflow-hidden">
        <div className="grid grid-cols-12 min-h-[640px]">
          {/* Left rail: conversations list */}
          <aside className="col-span-4 lg:col-span-3 border-r border-slate-100 overflow-y-auto max-h-[80vh]">
            {conversationsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No conversations yet. Inbound messages will appear here when a
                customer messages one of your channels.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {conversations.map((c) => {
                  const Icon = channelIcon(c.channel);
                  const isSelected = c.id === selectedId;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(c.id)}
                        className={`w-full text-left px-4 py-3 transition-colors ${
                          isSelected ? "bg-slate-100" : "hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="h-9 w-9 shrink-0 rounded-md bg-primary/10 flex items-center justify-center">
                            <Icon className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium text-sm truncate">
                                {c.customer_identifier}
                              </p>
                              <span className="text-xs text-muted-foreground shrink-0">
                                {formatRelativeTime(c.last_message_at)}
                              </span>
                            </div>
                            {c.subject && (
                              <p className="text-xs text-muted-foreground truncate">
                                {c.subject}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-[10px] capitalize">
                                {c.channel}
                              </Badge>
                              {c.unread_count > 0 && (
                                <Badge variant="default" className="text-[10px]">
                                  {c.unread_count} unread
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* ME40 — the count is the point. Without it the rail looks
                complete at 100 and the customer never learns that #101 exists.
                Shown whenever anything is loaded, not only when more remain,
                so "showing everything" is stated rather than assumed. */}
            {!conversationsLoading && conversations.length > 0 ? (
              <div className="border-t border-slate-100 px-4 py-3 text-center">
                <p className="text-xs text-muted-foreground">
                  Showing {conversations.length} of {conversationsTotal}
                </p>
                {conversations.length < conversationsTotal ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 h-7 text-xs"
                    disabled={loadingMore}
                    onClick={loadMoreConversations}
                  >
                    {loadingMore ? (
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    ) : null}
                    Load more
                  </Button>
                ) : null}
              </div>
            ) : null}
          </aside>

          {/* Right pane: thread + reply */}
          <section className="col-span-8 lg:col-span-9 flex flex-col max-h-[80vh]">
            {!selectedConversation ? (
              <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                Select a conversation to view its messages.
              </div>
            ) : (
              <>
                {/* Thread header */}
                <header className="border-b border-slate-100 px-6 py-4">
                  <h2 className="text-lg font-semibold">
                    {selectedConversation.customer_identifier}
                  </h2>
                  <p className="text-xs text-muted-foreground capitalize">
                    {selectedConversation.channel} · {selectedConversation.status}
                  </p>
                </header>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                  {messagesLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : messages.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No messages in this conversation yet.
                    </p>
                  ) : (
                    messages.map((m) => {
                      const outbound = m.direction === "outbound";
                      const failed = outbound && m.status === "failed";
                      // The API types this loosely (Array<Record<string, unknown>>),
                      // so narrow to the documented {url, filename, ...} shape and
                      // drop anything without a url — a link to `undefined` is
                      // worse than no link.
                      const attachments = (m.attachments ?? [])
                        .map((a) => a as { url?: string; filename?: string })
                        .filter((a) => typeof a.url === "string" && a.url.length > 0);
                      return (
                        <div
                          key={m.id}
                          className={`flex ${outbound ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                              outbound
                                ? "bg-primary text-primary-foreground"
                                : "bg-slate-100 text-slate-900"
                            }`}
                          >
                            {m.sender_name && !outbound && (
                              <p className="text-[11px] font-semibold opacity-80 mb-0.5">
                                {m.sender_name}
                              </p>
                            )}
                            {m.content ? (
                              <p className="text-sm whitespace-pre-wrap break-words">
                                {m.content}
                              </p>
                            ) : null}

                            {/* ME41 — attachments were rendered as an italic
                                "[document]" placeholder, so a Haesloop invoice
                                PDF looked like a bug rather than a file. The API
                                has returned {url, filename, content_type, size}
                                all along; nothing rendered it. */}
                            {attachments.length > 0
                              ? attachments.map((att, i) => (
                                  <a
                                    key={att.url ?? i}
                                    href={att.url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className={`mt-1 flex items-center gap-1.5 text-sm underline underline-offset-2 break-all ${
                                      outbound ? "opacity-90" : "text-slate-700"
                                    }`}
                                  >
                                    <Paperclip className="h-3.5 w-3.5 shrink-0" />
                                    {att.filename || "Attachment"}
                                  </a>
                                ))
                              : null}

                            {/* Neither text nor a usable attachment — say what
                                it is rather than pretending there is content. */}
                            {!m.content && attachments.length === 0 ? (
                              <p className="text-sm italic opacity-70">
                                [{m.content_type}]
                              </p>
                            ) : null}
                            <p
                              className={`text-[10px] mt-1 ${
                                outbound ? "opacity-70" : "text-muted-foreground"
                              }`}
                            >
                              {formatRelativeTime(m.sent_at)}
                              {m.status && outbound ? ` · ${m.status}` : ""}
                            </p>

                            {/* ME41 — a failed send showed only "· failed": no
                                reason, no way to act. The API has returned
                                error_message all along. Surfacing it plus a
                                retry is the difference between the user fixing
                                it themselves and filing a ticket. */}
                            {failed ? (
                              <div className="mt-2 rounded-lg bg-red-50 px-2.5 py-2 text-slate-900">
                                <p className="flex items-start gap-1.5 text-[11px] font-medium text-red-700">
                                  <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
                                  <span className="break-words">
                                    {m.error_message ||
                                      "Delivery failed — no reason was reported by the channel."}
                                  </span>
                                </p>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-2 h-7 bg-white text-xs"
                                  disabled={retryingId === m.id}
                                  onClick={() => handleRetry(m)}
                                >
                                  {retryingId === m.id ? (
                                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                  ) : (
                                    <RotateCcw className="mr-1 h-3 w-3" />
                                  )}
                                  Retry
                                </Button>
                                {/* Retry alone cannot clear an out-of-window
                                    rejection — the resend is the same
                                    disallowed free-form message. This is the
                                    path Meta actually permits. */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-2 ml-2 h-7 bg-white text-xs"
                                  onClick={() => setTemplateDialogOpen(true)}
                                >
                                  <FileText className="mr-1 h-3 w-3" />
                                  Send template
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply composer */}
                <div className="border-t border-slate-100 px-6 py-3">
                  <div className="flex gap-2">
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={handleReplyKey}
                      placeholder="Type a reply… (Enter to send, Shift+Enter for new line)"
                      rows={2}
                      className="resize-none"
                      disabled={sending}
                    />
                    <Button
                      onClick={handleSend}
                      disabled={sending || !replyText.trim()}
                      className="self-end"
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send
                    </Button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>

      {/* ME41 follow-up — out-of-window recovery. Rendered once for the open
          conversation rather than per message: the template is a property of
          the CONVERSATION's window state, not of the individual failure. */}
      {token && organisationId && selectedId ? (
        <TemplateSendDialog
          open={templateDialogOpen}
          onClose={() => setTemplateDialogOpen(false)}
          token={token}
          organisationId={organisationId}
          conversationId={selectedId}
          onSent={() => {
            window.setTimeout(() => fetchMessages(selectedId, true), 1_000);
          }}
        />
      ) : null}
    </div>
  );
}
